/**
 * Chat Store
 * Zustand store for chat state management
 * Matches main app's state management pattern
 */

import { create } from 'zustand';
import type { ChatStore, Message, ChatConfig } from '../types/chat';
import { chatApi, describeEducationChatError, initChatApi, type ConversationHistoryMessage, type EducationAudienceType, type EducationConversation, type EducationScopeType } from '../api/client';
import { t } from '@/lib/i18n';
import { getApiProfile } from '@/lib/runtimeConfig';
import type { FormField } from '@/types';

const LAST_CONVERSATION_ID_STORAGE_KEY = 'xedu_last_conversation_id_v1';
const LAST_CONVERSATION_ID_STORAGE_KEY_ADHD = 'xedu_last_conversation_id_adhd_v1';
const LAST_AUTH_TOKEN_STORAGE_KEY = 'xedu_auth_token_v1';
const LAST_AUTH_TOKEN_STORAGE_KEY_ADHD = 'xedu_auth_token_adhd_v1';
let lastHostSessionToken = '';

function isAdhdExternalProfile(profile?: string | null): boolean {
  const normalized = String(profile || '').trim().toLowerCase();
  if (normalized) {
    return normalized === 'adhd_external' || normalized === 'adhd';
  }
  return getApiProfile() === 'adhd_external';
}

function getConversationStorageKey(profile?: string | null): string {
  return isAdhdExternalProfile(profile)
    ? LAST_CONVERSATION_ID_STORAGE_KEY_ADHD
    : LAST_CONVERSATION_ID_STORAGE_KEY;
}

function getAuthTokenStorageKey(profile?: string | null): string {
  return isAdhdExternalProfile(profile)
    ? LAST_AUTH_TOKEN_STORAGE_KEY_ADHD
    : LAST_AUTH_TOKEN_STORAGE_KEY;
}

function buildPublicAdhdUser(user?: any) {
  const userId =
    typeof user?.id === 'string'
      ? user.id
      : typeof user?.user_id === 'string'
        ? user.user_id
        : typeof user?.userId === 'string'
          ? user.userId
          : '';

  return {
    ...(user && typeof user === 'object' ? user : {}),
    id: userId,
    user_id: userId,
    user_type: 'public_user',
    roles: ['public_user'],
  };
}

function isUuidLike(value: unknown): boolean {
  const s = (typeof value === 'string' ? value : '').trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}

function readPersistedConversationId(profile?: string | null): string | null {
  if (!isAdhdExternalProfile(profile)) return null;
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const raw = window.localStorage.getItem(getConversationStorageKey(profile));
    return isUuidLike(raw) ? raw!.trim() : null;
  } catch {
    return null;
  }
}

function persistConversationId(conversationId: string | null | undefined, profile?: string | null) {
  if (!isAdhdExternalProfile(profile)) return;
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const storageKey = getConversationStorageKey(profile);
    if (isUuidLike(conversationId)) {
      window.localStorage.setItem(storageKey, String(conversationId).trim());
      return;
    }
    window.localStorage.removeItem(storageKey);
  } catch {
    // ignore storage failures
  }
}

function persistToken(token: string | null | undefined, profile?: string | null) {
  if (!isAdhdExternalProfile(profile)) return;
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const storageKey = getAuthTokenStorageKey(profile);
    if (token && token.length > 8) {
      window.localStorage.setItem(storageKey, token.trim());
    } else {
      window.localStorage.removeItem(storageKey);
    }
  } catch {
    // ignore storage failures
  }
}

function toMessageFromHistoryItem(conversationId: string, item: ConversationHistoryMessage): Message {
  const date = new Date(item.timestamp);
  const safeDate = Number.isNaN(date.getTime()) ? new Date() : date;
  const messageId = (item.messageId || '').trim();
  const embeddedComponent =
    item.embeddedComponent && typeof item.embeddedComponent === 'object'
      ? item.embeddedComponent
      : undefined;

  return {
    id: messageId || `msg_${safeDate.getTime()}_${Math.random().toString(36).substr(2, 9)}`,
    role: item.role,
    content: item.content,
    timestamp: safeDate,
    conversationId,
    ...(embeddedComponent ? { embeddedComponent } : {}),
    metadata: {
      senderId: item.senderId,
      senderName: item.senderName,
      senderType: item.senderType,
    },
  };
}

function dedupeAndMergeHistoryMessages(historyMessages: Message[], existingMessages: Message[]): Message[] {
  const keyOf = (msg: Message): string => {
    const ts = msg.timestamp instanceof Date ? msg.timestamp.toISOString() : new Date(msg.timestamp).toISOString();
    return `${msg.role}|${msg.content}|${ts}`;
  };

  const seen = new Set<string>();
  const merged: Message[] = [];

  for (const msg of historyMessages) {
    const key = keyOf(msg);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(msg);
  }

  for (const msg of existingMessages) {
    const key = keyOf(msg);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(msg);
  }

  return merged;
}

function shouldApplyHydratedHistory(state: ChatStoreState): boolean {
  // History hydration is a background convenience. If a user send is already
  // in flight, applying old history can look like an extra assistant response.
  return !state.isLoading;
}

function buildIntakeFormMessage(payload: Record<string, any>) {
  return `[INTAKE_FORM_SUBMISSION] ${JSON.stringify(payload ?? {})}`;
}

function navigateToCsRouteAfterSubmit() {
  try {
    if (typeof window === 'undefined') return;
    const desired = '/';
    if (window.location.pathname !== desired) {
      window.history.replaceState({}, '', desired);
    }
  } catch {
    // ignore
  }
}

function buildIntakeEmbeddedComponent(opts: {
  onSubmit: (payload: Record<string, any>) => Promise<void>;
}) {
  return {
    type: 'form' as const,
    data: {
      id: 'intake_form_v1',
      title: t('chat.intakeForm.title', 'Intake form'),
      description: t(
        'chat.intakeForm.description',
        'Fill in what you can — you can leave anything blank and continue in chat.'
      ),
      fields: [
        { name: 'full_name', label: t('chat.intakeForm.fullName', 'Full name'), type: 'text', required: false },
        { name: 'date_of_birth', label: t('chat.intakeForm.dob', 'Date of birth'), type: 'date', required: false },
        { name: 'email', label: t('chat.intakeForm.email', 'Email'), type: 'email', required: false },
        { name: 'phone', label: t('chat.intakeForm.phone', 'Phone'), type: 'tel', required: false },
        { name: 'suburb', label: t('chat.intakeForm.suburb', 'Suburb'), type: 'text', required: false },
        {
          name: 'postcode',
          label: t('chat.intakeForm.postcode', 'Postcode'),
          type: 'text',
          required: false,
          validation: { pattern: '^\\d{4}$' },
        },
        {
          name: 'funding_source',
          label: t('chat.intakeForm.funding', 'Funding source'),
          type: 'select',
          required: false,
          options: [
            { label: 'NDIS', value: 'NDIS' },
            { label: 'Medicare', value: 'Medicare' },
            { label: 'Private', value: 'Private' },
            { label: 'Insurance', value: 'Insurance' },
            { label: 'Other', value: 'Other' },
          ],
        },
        { name: 'ndis_number', label: t('chat.intakeForm.ndisNumber', 'NDIS number'), type: 'text', required: false },
        {
          name: 'services_requested',
          label: t('chat.intakeForm.services', 'Services requested'),
          type: 'textarea',
          required: false,
        },
        {
          name: 'therapy_goals',
          label: t('chat.intakeForm.goals', 'Therapy goals'),
          type: 'textarea',
          required: false,
        },
      ],
      submitLabel: t('chat.intakeForm.submit', 'Send intake details'),
      onSubmit: opts.onSubmit,
    },
  };
}

function parseJsonMaybe<T = any>(value: unknown): T | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value !== 'string') return value as T;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  try {
    return JSON.parse(trimmed) as T;
  } catch {
    return undefined;
  }
}

function normalizeFieldType(rawType: unknown): FormField['type'] {
  const type = String(rawType ?? 'text').toLowerCase().trim();

  switch (type) {
    case 'json':
    case 'object':
    case 'map':
      return 'json';
    case 'text':
    case 'string':
    case 'short_text':
      return 'text';
    case 'textarea':
    case 'long_text':
      return 'textarea';
    case 'email':
      return 'email';
    case 'tel':
    case 'phone':
      return 'tel';
    case 'number':
    case 'integer':
    case 'float':
    case 'decimal':
      return 'number';
    case 'date':
    case 'dob':
      return 'date';
    case 'select':
    case 'dropdown':
      return 'select';
    case 'radio':
      return 'radio';
    case 'checkbox':
    case 'boolean':
      return 'checkbox';
    case 'file':
    case 'upload':
      return 'file';
    default:
      return 'text';
  }
}

function normalizeFieldOptions(rawField: any): FormField['options'] | undefined {
  const direct = rawField?.options;
  const fromJson = parseJsonMaybe<any>(rawField?.options_json ?? rawField?.optionsJson);
  const fromValidation = rawField?.validation?.options ?? rawField?.validation_json?.options;

  const candidate =
    (Array.isArray(direct) ? direct : undefined) ??
    (Array.isArray(fromJson) ? fromJson : undefined) ??
    (Array.isArray(fromValidation) ? fromValidation : undefined);

  if (!candidate) return undefined;

  return candidate
    .map((opt: any) => {
      if (opt && typeof opt === 'object') {
        const label = opt.label ?? opt.name ?? opt.title ?? opt.value;
        const value = opt.value ?? opt.id ?? opt.key ?? opt.label;
        if (label === undefined || value === undefined) return null;
        return { label: String(label), value: typeof value === 'number' ? value : String(value) };
      }
      return { label: String(opt), value: String(opt) };
    })
    .filter(Boolean) as Array<{ label: string; value: string | number }>;
}

function normalizeEmbeddedFormFields(rawFields: any[]): FormField[] {
  return rawFields
    .map((raw) => {
      const validationFromJson = parseJsonMaybe<any>(raw?.validation_json ?? raw?.validationJson);
      const validation = raw?.validation && typeof raw.validation === 'object' ? raw.validation : validationFromJson;

      const name = (raw?.name ?? raw?.field_key ?? raw?.fieldKey ?? '').toString().trim();
      const label = (raw?.label ?? raw?.field_label ?? raw?.fieldLabel ?? name).toString();
      if (!name) return null;

      const field: FormField = {
        name,
        label,
        type: normalizeFieldType(raw?.type ?? raw?.field_type ?? raw?.fieldType),
        required: Boolean(raw?.required ?? raw?.is_required ?? raw?.isRequired ?? false),
        placeholder: raw?.placeholder,
        helperText: raw?.helperText ?? raw?.help_text ?? raw?.helpText,
        defaultValue: raw?.defaultValue ?? raw?.default_value ?? raw?.default,
        disabled: Boolean(raw?.disabled ?? false),
        validation: validation && typeof validation === 'object' ? validation : undefined,
        options: normalizeFieldOptions({ ...raw, validation, validation_json: validationFromJson }),
      };

      return field;
    })
    .filter(Boolean) as FormField[];
}

export const __test__embeddedFormNormalization = {
  parseJsonMaybe,
  normalizeFieldType,
  normalizeFieldOptions,
  normalizeEmbeddedFormFields,
};

function getInviteTokenFromUrl(): string | null {
  try {
    if (typeof window === 'undefined') return null;
    const token = new URLSearchParams(window.location.search).get('invite');
    return token && token.trim() ? token.trim() : null;
  } catch {
    return null;
  }
}

interface ChatStoreState extends ChatStore {
  activeEducationConversation: EducationConversation | null;
  openEducationConversation: (conversationId: string) => Promise<void>;
  openEducationDestination: (scopeType: EducationScopeType, scopeId: string | null, audienceType: EducationAudienceType) => Promise<void>;
  refreshEducationHistory: () => Promise<void>;
  config: ChatConfig | null;
  disclaimerShown: boolean;
  suppressWelcomeFlow: boolean;
  routeBootstrapDone: boolean;
  postReadyBootstrapDone: boolean;
  hostInitReceived: boolean;
  authContext: {
    tenantId?: string;
    user?: any;
    roles?: string[];
    permissions?: string[];
  } | null;
  initialize: (config: ChatConfig) => void;
  bootstrapAfterReady: () => void;
  applyHostInit: (payload: {
    tenantId?: string;
    accessToken?: string;
    user?: any;
    roles?: string[];
    permissions?: string[];
    sessionExists?: boolean;
    conversationId?: string;
  }) => Promise<void>;
  initiateFromHost: (payload: any) => Promise<{
    conversationId?: string;
    helpHints: string[];
    topic: any;
  }>;
}

export const useChatStore = create<ChatStoreState>()(
  (set, get) => ({
    // State
    messages: [],
    isLoading: false,
    isTyping: false,
    isConnected: false,
    error: null,
    conversationId: null,
    sessionId: null,
    participants: [],
    config: null,
    disclaimerShown: false,
    suppressWelcomeFlow: false,
    routeBootstrapDone: false,
    postReadyBootstrapDone: false,
    hostInitReceived: false,
      // If the host triggers INITIATE before INIT has been applied (embedded mode requiring host init),
      // buffer it until we're ready to call the backend.
      _pendingHostInitiate: null as any,
    authContext: null,
    activeEducationConversation: null,
    openEducationConversation: async (conversationId: string) => {
      set({ isLoading: true, error: null });
      try {
        const conversation = await chatApi.getEducationConversation(conversationId);
        const history = await chatApi.getConversationMessages(conversationId, 100);
        set({
          conversationId,
          activeEducationConversation: conversation,
          messages: history.messages.map(item => toMessageFromHistoryItem(conversationId, item)),
          participants: [],
          isLoading: false,
          isTyping: false,
          disclaimerShown: true,
          suppressWelcomeFlow: true,
        });
      } catch (error) {
        set({ isLoading: false, activeEducationConversation: null, conversationId: null, messages: [], error: new Error(describeEducationChatError(error)) });
        throw error;
      }
    },
    openEducationDestination: async (scopeType, scopeId, audienceType) => {
      const conversation = await chatApi.createEducationConversation(scopeType, scopeId, audienceType);
      await get().openEducationConversation(conversation.conversation_id);
    },
    refreshEducationHistory: async () => {
      const { activeEducationConversation, isLoading } = get();
      if (!activeEducationConversation || isLoading) return;
      const conversationId = activeEducationConversation.conversation_id;
      try {
        await chatApi.getEducationConversation(conversationId);
        const history = await chatApi.getConversationMessages(conversationId, 100);
        if (get().activeEducationConversation?.conversation_id !== conversationId || get().isLoading) return;
        set({ messages: history.messages.map(item => toMessageFromHistoryItem(conversationId, item)) });
      } catch (error) {
        const status = (error as { status?: number })?.status;
        if (status === 401 || status === 403 || status === 404) {
          set({ activeEducationConversation: null, conversationId: null, messages: [], error: new Error('This conversation is no longer available. Reopen the assistant or choose another conversation.') });
        }
      }
    },

    bootstrapAfterReady: () => {
      const { config, postReadyBootstrapDone } = get();
      if (!config) return;
      const profile = config.apiProfile;

      // If embedded + host INIT is required, delay API bootstrapping.
      if (config.behavior?.requireHostInit && !get().hostInitReceived) {
        return;
      }

      if (postReadyBootstrapDone) return;
      set({ postReadyBootstrapDone: true });

      // Try to hydrate recent history when a known conversation exists.
      // This lets users catch up immediately after opening chat.
      void (async () => {
        const knownConversationId = get().conversationId || readPersistedConversationId(profile);
        if (!knownConversationId) return;

        try {
          const history = await chatApi.getConversationMessages(knownConversationId, 20);
          if (!Array.isArray(history.messages) || history.messages.length === 0) {
            set({ conversationId: knownConversationId });
            return;
          }

          const historyMessages = history.messages.map((item) =>
            toMessageFromHistoryItem(knownConversationId, item)
          );

          set((state) => {
            if (!shouldApplyHydratedHistory(state)) {
              return {};
            }
            return {
              conversationId: knownConversationId,
              messages: dedupeAndMergeHistoryMessages(historyMessages, state.messages),
              disclaimerShown: true,
            };
          });
          persistConversationId(knownConversationId, profile);
        } catch {
          // Non-fatal: fallback to normal welcome flow.
        }
      })();

      // If the page was opened with an invite token, join that conversation as staff.
      void (async () => {
        const token = getInviteTokenFromUrl();
        if (!token) return;

        try {
          const joined = await chatApi.acceptInvite(token);
          if (!joined?.conversationId) return;

          set({ conversationId: joined.conversationId });
          persistConversationId(joined.conversationId, profile);

          const msg: Message = {
            id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            role: 'system',
            content: t('chat.invite.joined', 'You joined this conversation via an invite link.'),
            timestamp: new Date(),
          };
          set((s) => ({ messages: [...s.messages, msg] }));

          await get().refreshParticipants();
        } catch {
          // Ignore invite errors; user can continue as normal.
        }
      })();

      // Show disclaimer once and load initial welcome message.
      void (async () => {
        console.log('[Bootstrap] Starting welcome message flow...');
        try {
          if (get().suppressWelcomeFlow) {
            // Secondary iframe: authenticate silently and wait for INITIATE context.
            return;
          }
          if (get().messages.length === 0) {
            const knownConversationId = get().conversationId || readPersistedConversationId(profile);
            if (knownConversationId) {
              try {
                const history = await chatApi.getConversationMessages(knownConversationId, 20);
                if (Array.isArray(history.messages) && history.messages.length > 0) {
                  const historyMessages = history.messages.map((item) =>
                    toMessageFromHistoryItem(knownConversationId, item)
                  );
                  set((state) => {
                    if (!shouldApplyHydratedHistory(state)) {
                      return {};
                    }
                    return {
                      conversationId: knownConversationId,
                      messages: historyMessages,
                      disclaimerShown: true,
                    };
                  });
                  persistConversationId(knownConversationId, profile);
                  return;
                }
              } catch {
                // Fall through to disclaimer/start flow.
              }
            }
          }
          if (get().messages.length > 0) {
            return;
          }
          if (!get().disclaimerShown) {
            const disclaimer: Message = {
              id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              role: 'system',
              content: t(
                'chat.disclaimer.trial',
                'XEdu AI can help with school information and next steps. Admissions decisions and messages to families remain subject to school staff approval.'
              ),
              timestamp: new Date(),
            };

            set((s) => ({
              messages: s.messages.length === 0 ? [disclaimer] : s.messages,
              disclaimerShown: true,
            }));
            config.callbacks?.onMessage?.(disclaimer);
          }

          console.log('[Bootstrap] Calling startConversation...');
          const start = await chatApi.startConversation();
          console.log('[Bootstrap] startConversation returned:', start);
          if (start?.conversationId) {
            set({ conversationId: start.conversationId });
            persistConversationId(start.conversationId, profile);
          }
          if (!start?.message) return;

          const msg: Message = {
            id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            role: 'assistant',
            content: start.message,
            timestamp: new Date(),
            conversationId: start.conversationId,
          };

          console.log('[Bootstrap] Adding welcome message to store:', msg);
          set((s) => {
            if (s.messages.length === 0) return { messages: [msg] };
            // If the disclaimer is present, append the welcome message after it.
            if (s.messages.length === 1 && s.messages[0]?.role === 'system') {
              return { messages: [...s.messages, msg] };
            }
            return {};
          });
          config.callbacks?.onMessage?.(msg);
        } catch {
          // If this fails, we fall back to the existing empty-state UI.
        }
      })();
    },

    // Actions
    initialize: (config: ChatConfig) => {
      set({ config });
      const isAdhdProfile = isAdhdExternalProfile(config.apiProfile);

      // Defensive: ensure API client is initialized before any route bootstrap
      // (avoids dev/HMR timing or embedding order issues).
      try {
        initChatApi({
          baseURL: config.apiUrl,
          profile: config.apiProfile,
          sector: config.sector || 'xedu',
          headers: {
            'X-Tenant-ID': config.tenantId,
            ...(config.apiKey ? { 'X-API-Key': config.apiKey } : {}),
          },
        });
        if (isAdhdProfile) {
          chatApi.clearAuthToken();
          persistToken(null, config.apiProfile);
        }
      } catch {
        // ignore
      }

      // One-time route bootstrap for web.
      // - / is the default
      // - /intake auto-opens the embedded intake form (for testing)
      const stateNow = get();
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      const isIntake =
        path === '/intake' ||
        path.startsWith('/intake/') ||
        path === '/chat/cs/intake' ||
        path.startsWith('/chat/cs/intake/');

      // In dev/HMR, zustand state can persist across reloads; ensure /intake remains a reliable
      // fast-test route by attempting to load the dynamic form even if routeBootstrapDone is true.
      const alreadyHasDynamicIntakeForm = stateNow.messages.some((m) => {
        const id = (m as any)?.embeddedComponent?.data?.id;
        return id === 'intake_form_dynamic' || id === 'intake_form_db';
      });

      if (isIntake && !alreadyHasDynamicIntakeForm) {
        void (async () => {
          const submitHandler = async (payload: any) => {
            await get().sendMessage(buildIntakeFormMessage(payload));
            navigateToCsRouteAfterSubmit();
          };

          try {
            const response = await chatApi.sendMessage({
              message: 'open intake form',
              conversationId: undefined,
              sessionId: undefined,
            });

            if (response.uiAction) {
              config.callbacks?.onUiAction?.(response.uiAction);
            }

            const serverComponent: any = response.embeddedComponent;
            const embeddedComponent =
              serverComponent?.type === 'form' &&
              serverComponent?.data &&
              Array.isArray(serverComponent.data.fields) &&
              serverComponent.data.fields.length > 0
                ? (() => {
                    const normalizedFields = normalizeEmbeddedFormFields(serverComponent.data.fields);
                    return {
                      type: 'form' as const,
                      data: {
                        id: String(serverComponent.data.id ?? serverComponent.data.formType ?? 'intake_form_db'),
                        title: String(serverComponent.data.title ?? t('chat.intakeForm.title', 'Intake form')),
                        description: serverComponent.data.description
                          ? String(serverComponent.data.description)
                          : undefined,
                        fields: normalizedFields.length > 0 ? normalizedFields : serverComponent.data.fields,
                        submitLabel: serverComponent.data.submitLabel
                          ? String(serverComponent.data.submitLabel)
                          : serverComponent.data.submit_label
                            ? String(serverComponent.data.submit_label)
                            : undefined,
                        onSubmit: submitHandler,
                      },
                    };
                  })()
                : buildIntakeEmbeddedComponent({ onSubmit: submitHandler });

            const msg: Message = {
              id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              role: 'assistant',
              content: response.message || t('chat.intakeForm.testRouteIntro', 'Intake form loaded.'),
              timestamp: new Date(response.timestamp),
              conversationId: response.conversationId,
              embeddedComponent,
              metadata: {
                senderName: response.senderName || t('chat.sender.ai', 'AI'),
                senderId: response.senderId || 'ai_agent',
                senderType: 'ai_agent',
              },
            };

            set({ conversationId: response.conversationId });
            persistConversationId(response.conversationId, config.apiProfile);
            set((s) => ({ messages: [...s.messages, msg] }));
            config.callbacks?.onMessage?.(msg);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('[Intake route] Failed to load dynamic intake form, falling back.', e);

            const errMsg =
              e instanceof Error
                ? e.message
                : typeof e === 'string'
                  ? e
                  : 'Unknown error';

            const embeddedComponent = buildIntakeEmbeddedComponent({ onSubmit: submitHandler });

            const msg: Message = {
              id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              role: 'assistant',
              content: t(
                'chat.intakeForm.testRouteFallback',
                `Failed to load the dynamic intake form from the server; showing the fallback short form. (apiUrl='${config.apiUrl}', error='${errMsg}')`
              ),
              timestamp: new Date(),
              embeddedComponent,
              metadata: {
                senderName: t('chat.sender.ai', 'AI'),
                senderId: 'ai_agent',
                senderType: 'ai_agent',
              },
            };
            set((s) => ({ messages: [...s.messages, msg] }));
          }
        })();
      }

      if (!stateNow.routeBootstrapDone) {
        set({ routeBootstrapDone: true });
      }

      // Generate session ID
      const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      set({ sessionId });

      // Call ready callback
      config.callbacks?.onReady?.();

      get().bootstrapAfterReady();
    },

    applyHostInit: async (payload: {
      tenantId?: string;
      accessToken?: string;
      user?: any;
      roles?: string[];
      permissions?: string[];
      sessionExists?: boolean;
      conversationId?: string;
    }) => {
      const config = get().config;
      if (!config) {
        throw new Error(t('chat.error.notInitialized', 'Chat not initialized'));
      }
      const isAdhdProfile = isAdhdExternalProfile(config.apiProfile);

      const tenantId = (payload.tenantId || config.tenantId || '').trim();
      let token = isAdhdProfile ? '' : (payload.accessToken || '').trim();

      // Hosts sometimes send tokens in slightly different formats:
      // - Raw JWT: "eyJ..."
      // - Prefixed: "Bearer eyJ..."
      // - Quoted: "'eyJ...'" or "\"eyJ...\""
      // Normalize to the raw JWT string.
      if (token.toLowerCase().startsWith('bearer ')) {
        token = token.slice(7).trim();
      }
      if (
        (token.startsWith('"') && token.endsWith('"')) ||
        (token.startsWith("'") && token.endsWith("'"))
      ) {
        token = token.slice(1, -1).trim();
      }

      const previous = get().authContext;
      const nextUserId = String(payload.user?.id || payload.user?.user_id || '');
      if (previous && (previous.tenantId !== tenantId || String(previous.user?.id || previous.user?.user_id || '') !== nextUserId || token !== lastHostSessionToken)) {
        set({ messages: [], conversationId: null, activeEducationConversation: null, participants: [] });
        chatApi.clearAuthToken();
      }
      lastHostSessionToken = token;

      if (tenantId) {
        chatApi.setTenantId(tenantId);
      }
      if (token) {
        chatApi.setAuthToken(token);
        persistToken(token, config.apiProfile);
      } else {
        chatApi.clearAuthToken();
        persistToken(null, config.apiProfile);
      }

      const hostConversationId = isUuidLike(payload.conversationId)
        ? String(payload.conversationId).trim()
        : null;
      const knownConversationId = hostConversationId;
      if (!hostConversationId) {
        persistConversationId(null, config.apiProfile);
        set({ conversationId: null });
      }

      const tryHydrateConversationHistory = async () => {
        if (!knownConversationId) return false;

        try {
          const history = await chatApi.getConversationMessages(knownConversationId, 20);
          if (!Array.isArray(history.messages) || history.messages.length === 0) {
            set({ conversationId: knownConversationId });
            persistConversationId(knownConversationId, config.apiProfile);
            return false;
          }

          const historyMessages = history.messages.map((item) =>
            toMessageFromHistoryItem(knownConversationId, item)
          );

          set((state) => {
            if (!shouldApplyHydratedHistory(state)) {
              return {};
            }
            return {
              conversationId: knownConversationId,
              messages: historyMessages,
              disclaimerShown: true,
            };
          });
          persistConversationId(knownConversationId, config.apiProfile);
          return true;
        } catch {
          return false;
        }
      };

      if (isAdhdProfile) {
        const publicUser = buildPublicAdhdUser(payload.user);
        const authCtx = {
          tenantId,
          user: publicUser,
          roles: ['public_user'],
          permissions: [],
        };
        set({ authContext: authCtx, hostInitReceived: true });
        chatApi.setUserContext(publicUser);

        const hydrated = await tryHydrateConversationHistory();
        if (hydrated) {
          set({ suppressWelcomeFlow: true });
        }

        get().bootstrapAfterReady();

        const pending = (get() as any)._pendingHostInitiate;
        if (pending) {
          set({ _pendingHostInitiate: null as any } as any);
          try {
            await get().initiateFromHost(pending);
          } catch {
            // ignore
          }
        }

        return;
      }

      // Host may indicate another iframe already established a session.
      // In that case we skip the welcome flow (no disclaimer, no startConversation)
      // and wait for INITIATE context.
      set({ suppressWelcomeFlow: Boolean(token) || payload.sessionExists === true });

      set({ hostInitReceived: true });

      // If the host didn't provide a token, don't call /auth/me.
      // API-key session tokens are opaque UUIDs, not JWTs; /auth/me is for user
      // JWT sessions and will 401 for these embedded chat sessions.
      // The embed can operate anonymously; /auth/me would just 401.
      const tokenLooksLikeOpaqueSession = isUuidLike(token);
      if (!token || tokenLooksLikeOpaqueSession) {
        const payloadRoles = Array.isArray(payload.roles)
          ? payload.roles
          : Array.isArray((payload.user as any)?.roles)
            ? (payload.user as any).roles
            : [];
        const payloadPermissions = Array.isArray(payload.permissions)
          ? payload.permissions
          : Array.isArray((payload.user as any)?.permissions)
            ? (payload.user as any).permissions
            : [];
        const authCtx = {
          tenantId: tenantId,
          user: payload.user || undefined,
          roles: payloadRoles,
          permissions: payloadPermissions,
        };
        set({ authContext: authCtx });

        // Set user context in API client for requests
        if (payload.user) {
          chatApi.setUserContext(payload.user);
        }

        const hydrated = await tryHydrateConversationHistory();
        if (hydrated) {
          set({ suppressWelcomeFlow: true });
        }

        get().bootstrapAfterReady();

        // Process any buffered INITIATE once tenant is set.
        const pending = (get() as any)._pendingHostInitiate;
        if (pending) {
          set({ _pendingHostInitiate: null as any } as any);
          try {
            await get().initiateFromHost(pending);
          } catch {
            // ignore
          }
        }

        return;
      }

      try {
        const me = await chatApi.getAuthMe();

        // Merge user identity: /auth/me is the authority for id/email/name when it
        // returns a real UUID. If /auth/me returns a stub (non-UUID id, e.g. "dev-user"
        // from SERVICE_TO_SERVICE_TOKEN_OFF), prefer the host INIT payload's id instead.
        const _isUuidLike = (v: unknown): boolean => {
          const s = (typeof v === 'string' ? v : '').trim();
          return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
        };
        const meUserId = (me?.user as any)?.id;
        const initUserId = (payload.user as any)?.id;
        // If /auth/me returned a stub id and the INIT payload has a real UUID, prefer the INIT one.
        const resolvedUserId =
          _isUuidLike(meUserId)
            ? meUserId
            : _isUuidLike(initUserId)
              ? initUserId
              : meUserId;

        const authCtx = {
          tenantId: me?.tenant?.id || tenantId,
          user: {
            ...(payload.user || {}),   // Start with INIT payload (has user_type, roles, etc.)
            ...(me?.user || {}),       // Overlay with /auth/me data (email, name, etc.)
            id: resolvedUserId,        // Resolved id: real UUID wins over stub
          },
          roles: me?.roles || [],
          permissions: me?.permissions || [],
        };
        set({ authContext: authCtx });

        // Set user context in API client for requests
        if (authCtx.user) {
          chatApi.setUserContext(authCtx.user);
        }
      } catch (e) {
        const anyErr = e as any;
        const status = typeof anyErr?.status === 'number' ? anyErr.status : undefined;

        // Non-fatal: unauthenticated embed or older backends.
        if (status === 401 || status === 403 || status === 404) {
          const authCtx = {
            tenantId: tenantId,
            user: payload.user || undefined,
            roles: [],
            permissions: [],
          };
          set({ authContext: authCtx });

          // Set user context in API client for requests
          if (payload.user) {
            chatApi.setUserContext(payload.user);
          }
        } else {
          const err = e instanceof Error ? e : new Error('Failed to load /auth/me');
          set({ error: err });
          config.callbacks?.onError?.(err);
        }
      }

      const hydrated = await tryHydrateConversationHistory();
      if (hydrated) {
        set({ suppressWelcomeFlow: true });
      }

      get().bootstrapAfterReady();

      // Process any buffered INITIATE once auth/tenant is set.
      const pending = (get() as any)._pendingHostInitiate;
      if (pending) {
        set({ _pendingHostInitiate: null as any } as any);
        try {
          await get().initiateFromHost(pending);
        } catch {
          // ignore
        }
      }
    },

    initiateFromHost: async (payload: any) => {
      const { config, conversationId, sessionId } = get();
      if (!config) {
        throw new Error(t('chat.error.notInitialized', 'Chat not initialized'));
      }

      // Important: allow INITIATE even before INIT.
      // Some hosts send INITIATE first (context-only) and INIT later (auth/theme).
      // We can still fetch anonymous, database-backed help_hints using the default tenant.

      // Extract AiAssistantContext-like object.
      const ctx =
        payload && typeof payload === 'object'
          ? (payload.context && typeof payload.context === 'object' ? payload.context : payload)
          : undefined;

      const scope = ctx?.education_scope;
      if (scope && typeof scope === 'object' && typeof scope.type === 'string') {
        const scopeId = typeof scope.id === 'string' ? scope.id : null;
        await get().openEducationDestination(scope.type as EducationScopeType, scopeId, 'private_ai');
      }

      try {
        // Host-initiated proactive help: ask backend to run Step 1 intent routing
        // and return database-backed help_hints for the routed topic/task.
        // We do NOT append any visible chat message here. INITIATE is contextual
        // host bootstrapping/help-hint discovery, not the user's submitted chat
        // turn. Rendering it in the transcript races with a real user send and
        // can look like duplicate assistant answers for one input.
        const response = await chatApi.sendMessage({
          protocol: 'INITIATE',
          // Backward-compat: some backend deployments still validate message min_length=1.
          // The server-side INITIATE flow ignores the message content.
          message: 'Initiate',
          conversationId: get().conversationId || conversationId || undefined,
          sessionId: sessionId || undefined,
          context: ctx,
        });

        set((s) => ({
          conversationId: s.conversationId || response.conversationId || undefined,
        }));

        return {
          conversationId: response.conversationId || conversationId || undefined,
          helpHints: Array.isArray(response.suggestions) ? response.suggestions : [],
          topic: response.topic,
        };
      } catch (e) {
        const err = e instanceof Error ? e : new Error('Failed to initiate help hints');
        set({ error: err });
        config.callbacks?.onError?.(err);
        throw err;
      }
    },

    refreshParticipants: async () => {
      const { conversationId } = get();
      if (!conversationId) {
        set({ participants: [] });
        return;
      }

      try {
        const res = await chatApi.getParticipants(conversationId);
        set({ participants: res.participants || [] });
      } catch {
        // Non-fatal; participants are optional for 1:1 chat.
      }
    },

    createInviteLink: async () => {
      const { conversationId } = get();
      if (!conversationId) {
        const msg: Message = {
          id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          role: 'system',
          content: t('chat.invite.noConversation', 'Send a message first to start a conversation, then you can generate an invite link.'),
          timestamp: new Date(),
        };
        set((s) => ({ messages: [...s.messages, msg] }));
        return;
      }

      try {
        const res = await chatApi.createInvite(conversationId);
        if (!res?.inviteLink) return;

        const msg: Message = {
          id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          role: 'system',
          content: t(
            'chat.invite.link',
            `Invite link: ${res.inviteLink}`
          ),
          timestamp: new Date(),
        };
        set((s) => ({ messages: [...s.messages, msg] }));
      } catch (e) {
        const msg: Message = {
          id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          role: 'system',
          content: t(
            'chat.invite.failed',
            e instanceof Error ? `Failed to create invite link: ${e.message}` : 'Failed to create invite link.'
          ),
          timestamp: new Date(),
        };
        set((s) => ({ messages: [...s.messages, msg] }));
      }
    },

    sendMessage: async (content: string) => {
      const { messages, conversationId, sessionId, config } = get();

      if (!config) {
        throw new Error(t('chat.error.notInitialized', 'Chat not initialized'));
      }

      const educationConversation = get().activeEducationConversation;
      if (educationConversation && educationConversation.audience_type !== 'private_ai' && !content.trim().toLowerCase().startsWith('@ai')) {
        set({ isLoading: true, error: null });
        try {
          await chatApi.postEducationStaffMessage(educationConversation.conversation_id, content);
          const history = await chatApi.getConversationMessages(educationConversation.conversation_id, 100);
          set({ messages: history.messages.map(item => toMessageFromHistoryItem(educationConversation.conversation_id, item)), isLoading: false });
        } catch (error) {
          set({ isLoading: false, error: new Error(describeEducationChatError(error)) });
        }
        return;
      }

      // Add user message immediately
      const userMessage: Message = {
        id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        role: 'user',
        content,
        timestamp: new Date(),
        conversationId: conversationId || undefined,
        metadata: {
          senderName: t('chat.sender.you', 'You'),
          senderId: 'you',
          senderType: 'customer',
        },
      };

      set({
        messages: [...messages, userMessage],
        isLoading: true,
        isTyping: true,
        error: null,
      });

      // Call message callback
      config.callbacks?.onMessage?.(userMessage);

      try {
        // UX: remind user if backend is taking long.
        // These timers are cleared as soon as we receive a response or error.
        const fifteenSecondTimer = setTimeout(() => {
          const state = get();
          if (!state.isLoading) return;
          
          // First reminder: Processing data/info
          const processingPhrases = [
            "..."
          ];
          const randomPhrase = processingPhrases[Math.floor(Math.random() * processingPhrases.length)];
          
          const msg: Message = {
            id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            role: 'system',
            content: t('chat.delay.processing', randomPhrase),
            timestamp: new Date(),
          };
          set((s) => ({ messages: [...s.messages, msg] }));
        }, 15000);

        const fortySecondTimer = setTimeout(() => {
          const state = get();
          if (!state.isLoading) return;
          
          // Second reminder: Unexpected delay
          const delayPhrases = [
            "..."
          ];
          const randomDelayPhrase = delayPhrases[Math.floor(Math.random() * delayPhrases.length)];
          
          const msg: Message = {
            id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            role: 'system',
            content: t('chat.delay.longer', randomDelayPhrase),
            timestamp: new Date(),
          };
          set((s) => ({ messages: [...s.messages, msg] }));
        }, 40000);

        // Send to API
        const response = await chatApi.sendMessage({
          message: content,
          conversationId: conversationId || undefined,
          sessionId: sessionId || undefined,
        });

        if (response.uiAction) {
          config.callbacks?.onUiAction?.(response.uiAction);
        }

        clearTimeout(fifteenSecondTimer);
        clearTimeout(fortySecondTimer);

        // Add AI response
        const rawUiAction: any = response.uiAction;
        const intakeUiType =
          typeof rawUiAction === 'string'
            ? rawUiAction
            : rawUiAction?.type ?? rawUiAction?.action ?? rawUiAction?.name;

        const serverComponentAny: any = response.embeddedComponent;
        const serverWantsForm =
          serverComponentAny?.type === 'form' &&
          (Array.isArray(serverComponentAny?.data?.fields) ? serverComponentAny.data.fields.length > 0 : true);
        const shouldShowIntakeForm =
          (config.features?.embeddedForms ?? true) &&
          (intakeUiType === 'open_intake_form' || intakeUiType === 'offer_intake_form' || serverWantsForm);

        const submitHandler = async (payload: any) => {
          await get().sendMessage(buildIntakeFormMessage(payload));
          navigateToCsRouteAfterSubmit();
        };

        const embeddedComponent = shouldShowIntakeForm
          ? (() => {
              const serverComponent: any = response.embeddedComponent;
              if (
                serverComponent?.type === 'form' &&
                serverComponent?.data &&
                Array.isArray(serverComponent.data.fields) &&
                serverComponent.data.fields.length > 0
              ) {
                const normalizedFields = normalizeEmbeddedFormFields(serverComponent.data.fields);
                if (normalizedFields.length === 0) {
                  return buildIntakeEmbeddedComponent({
                    onSubmit: submitHandler,
                  });
                }

                return {
                  type: 'form' as const,
                  data: {
                    id: String(serverComponent.data.id ?? serverComponent.data.formType ?? 'intake_form_db'),
                    title: String(serverComponent.data.title ?? t('chat.intakeForm.title', 'Intake form')),
                    description: serverComponent.data.description
                      ? String(serverComponent.data.description)
                      : undefined,
                    fields: normalizedFields,
                    submitLabel: serverComponent.data.submitLabel
                      ? String(serverComponent.data.submitLabel)
                      : serverComponent.data.submit_label
                        ? String(serverComponent.data.submit_label)
                        : undefined,
                    onSubmit: submitHandler,
                  },
                };
              }

              return buildIntakeEmbeddedComponent({
                onSubmit: submitHandler,
              });
            })()
          : (() => {
              const serverComponent: any = response.embeddedComponent;
              if (serverComponent?.type !== 'form' || !serverComponent?.data) return response.embeddedComponent;

              const fieldsRaw = Array.isArray(serverComponent.data.fields) ? serverComponent.data.fields : [];
              const normalizedFields = fieldsRaw.length > 0 ? normalizeEmbeddedFormFields(fieldsRaw) : [];

              return {
                type: 'form' as const,
                data: {
                  id: String(serverComponent.data.id ?? serverComponent.data.formType ?? 'form'),
                  title: String(serverComponent.data.title ?? t('chat.intakeForm.title', 'Intake form')),
                  description: serverComponent.data.description ? String(serverComponent.data.description) : undefined,
                  fields: normalizedFields.length > 0 ? normalizedFields : fieldsRaw,
                  submitLabel: serverComponent.data.submitLabel
                    ? String(serverComponent.data.submitLabel)
                    : serverComponent.data.submit_label
                      ? String(serverComponent.data.submit_label)
                      : undefined,
                  onSubmit: submitHandler,
                },
              };
            })();

        const aiMessage: Message = {
          id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          role: 'assistant',
          content: response.message,
          timestamp: new Date(response.timestamp),
          conversationId: response.conversationId,
          embeddedComponent,
          metadata: {
            senderName: response.senderName || t('chat.sender.ai', 'AI'),
            senderId: response.senderId || 'ai_agent',
            senderType: 'ai_agent',
          },
        };

        set((state) => ({
          messages: [...state.messages, aiMessage],
          isLoading: false,
          isTyping: false,
          conversationId: response.conversationId,
        }));

            persistConversationId(response.conversationId, config.apiProfile);

        // Keep participants fresh once we have a conversation.
        if (response.conversationId) {
          void get().refreshParticipants();
        }

        // Call message callback
        config.callbacks?.onMessage?.(aiMessage);
      } catch (error) {
        // Ensure reminder timers don't fire after an error.
        // (If timers already fired, they simply added system messages.)
        const errorObj = educationConversation
          ? new Error(describeEducationChatError(error))
          : error instanceof Error
            ? error
            : new Error(t('chat.error.sendFailed', 'Failed to send message'));

        set({
          isLoading: false,
          isTyping: false,
          error: errorObj,
        });

        // Call error callback
        config.callbacks?.onError?.(errorObj);

        // Add error message
        const errorMessage: Message = {
          id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          role: 'system',
          content: t('chat.error.genericUserMessage', 'Sorry, I encountered an error. Please try again.'),
          timestamp: new Date(),
        };

        set((state) => ({
          messages: [...state.messages, errorMessage],
        }));
      }
    },

    clearMessages: () => {
      persistConversationId(null, get().config?.apiProfile);
      set({
        messages: [],
        conversationId: null,
        activeEducationConversation: null,
        error: null,
      });
    },

    setTyping: (isTyping: boolean) => {
      set({ isTyping });
      const { config } = get();
      config?.callbacks?.onTyping?.(isTyping);
    },

    setError: (error: Error | null) => {
      set({ error });
      if (error) {
        const { config } = get();
        config?.callbacks?.onError?.(error);
      }
    },

    reconnect: async () => {
      set({ isConnected: false, error: null });

      // Simulate reconnection
      await new Promise((resolve) => setTimeout(resolve, 1000));

      set({ isConnected: true });

      const { config } = get();
      config?.callbacks?.onConnectionChange?.(true);
    },
  })
);

// Selectors for better performance
export const selectMessages = (state: ChatStoreState) => state.messages;
export const selectIsLoading = (state: ChatStoreState) => state.isLoading;
export const selectIsTyping = (state: ChatStoreState) => state.isTyping;
export const selectError = (state: ChatStoreState) => state.error;
export const selectConversationId = (state: ChatStoreState) => state.conversationId;
export const selectParticipants = (state: ChatStoreState) => state.participants;
export const selectShowSenderNames = (state: ChatStoreState) => {
  const visible = (state.participants || []).filter(p => !p.isHidden);
  return visible.length > 2;
};
